import { useCreateOrganization } from "@/api/user";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PENDING_BETA_INVITE_CODE_KEY } from "@/constants/betaInvite";
import { ROUTES } from "@/constants/routes";
import { CenteredLayout } from "@/layouts/CenteredLayout";
import { nativeLoginOpts } from "@/native/auth";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import * as analytics from "@/utils/analytics";
import { useAuth0 } from "@auth0/auth0-react";
import { clearSignupUtm, peekSignupUtm } from "@cohesive/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters" })
    .max(64, {
      message: "Organization name exceeds maximum length of 64 characters.",
    })
    .refine((val) => !emojiRegex.test(val), {
      message: "Organization name cannot contain emojis.",
    }),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateOrganizationPage() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [pendingInviteCode] = useState(() => {
    if (typeof sessionStorage === "undefined") {
      return null as string | null;
    }
    return sessionStorage.getItem(PENDING_BETA_INVITE_CODE_KEY);
  });
  const [signupUtm] = useState(() => peekSignupUtm());
  const { user, loginWithRedirect } = useAuth0();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  const createOrganizationMutation = useCreateOrganization({
    onSuccess: async (organization) => {
      setIsRedirecting(true);

      analytics.trackOrganizationCreated({
        organizationId: organization.id,
        utm: signupUtm,
      });

      const usedBetaInvite = !!pendingInviteCode;
      if (usedBetaInvite) {
        sessionStorage.removeItem(PENDING_BETA_INVITE_CODE_KEY);
      }
      clearSignupUtm();

      const returnTo = usedBetaInvite
        ? ROUTES.HOME
        : ROUTES.ONBOARDING.SELECT_PLAN;

      await loginWithRedirect({
        authorizationParams: {
          organization: organization.auth0_id,
        },
        appState: {
          returnTo,
        },
        ...nativeLoginOpts(),
      });
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (createOrganizationMutation.isPending) {
      return;
    }

    createOrganizationMutation.mutate(
      {
        name: values.name,
        billing_email: user?.email,
        ...(pendingInviteCode ? { invite_code: pendingInviteCode } : {}),
        ...(signupUtm ? { utm: signupUtm } : {}),
      },
      {},
    );
  };

  const isLoading = createOrganizationMutation.isPending || isRedirecting;

  return (
    <>
      <title>Create an Organization</title>

      <CenteredLayout maxWidth="md">
        <div className="space-y-8">
          {/* Header with logo and title */}
          <div className="text-center space-y-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                delay: 0.3,
                type: "spring",
                stiffness: 100,
                damping: 15,
              }}
              className="flex justify-center"
            >
              <LogoIcon width="64" height="64" />
            </motion.div>
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
              }}
              transition={{
                delay: 0.5,
                duration: 0.4,
                ease: "easeOut",
              }}
            >
              <h1 className="text-4xl font-bold text-foreground tracking-medium">
                Welcome to Cohesive
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight">
                Create a new workspace to get started
              </p>
            </motion.div>
          </div>

          {/* Form card */}
          <div className="max-w-[416px] mx-auto space-y-4">
            <motion.div
              className="rounded-xl p-6 relative"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
              }}
              transition={{
                delay: 0.7,
                duration: 0.4,
                ease: "easeOut",
              }}
            >
              {/* Animated background */}
              <motion.div
                className="absolute inset-0 rounded-xl bg-muted dark:bg-muted/40 z-0 shadow-md"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                }}
                transition={{
                  delay: 0.9,
                  duration: 0.4,
                  ease: "easeOut",
                }}
              />

              {/* Form content */}
              <div className="relative z-10">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-6"
                  >
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium text-foreground">
                            Organization name
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="text"
                              placeholder="Enter your organization name"
                              className="w-full py-6 text-base mt-2"
                              autoFocus
                              disabled={isLoading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full py-6 text-base font-medium"
                      size="lg"
                      isLoading={isLoading}
                    >
                      Continue
                    </Button>
                  </form>
                </Form>
              </div>
            </motion.div>

            {/* Terms and privacy notice */}
            <motion.div
              className="text-sm text-muted-foreground text-center pt-4"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
              }}
              transition={{
                delay: 1.1,
                duration: 0.4,
                ease: "easeOut",
              }}
            >
              <p>By clicking "Continue", you agree to our</p>
              <div className="space-x-1">
                <a
                  href="https://cohesive.ai/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms of Use
                </a>
                <span>and</span>
                <a
                  href="https://cohesive.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </CenteredLayout>
    </>
  );
}
